import * as React from 'react';
import { useEffect, useState } from 'react';
import { useStyles } from './styles'; // Adjust the import path as needed
import { useNavigate } from 'react-router-dom';
import { ContentBlock } from '@/components';
import { getAttendants } from '@/api';
import { GoPlus } from 'react-icons/go';
import { Tabs } from '@/components/Tabs';
import { useAppData } from '@/zustand/useAppData';
import { useAlertData } from '@/zustand/useAlertData';
import { useAttendantListData } from '@/zustand/useAttendantListData';
import { AttendantListItem } from '@/zustand/interfaces/attendant-list-data';
import { hashStringWithSHA256, trackPageView } from '@/gtag/utils';

enum TabLabels {
  Active = 'Active',
  Inactive = 'Inactive'
}

export const AttendantManagement: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useAppData((store) => store.appData);
  const setAlert = useAlertData((store) => store.setAlertData);
  const { active, inactive, setAttendantListData } = useAttendantListData(
    (store) => store
  );

  const [activeTab, setActiveTab] = useState<string>(TabLabels.Active);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchAttendants = async () => {
    setLoading(true);
    try {
      const attendants = await getAttendants(id);
      // set the state
      setAttendantListData(attendants);
    } catch (error) {
      setAlert({
        variation: 'error',
        text: <span>Unable to get store attendants: Please try again</span>
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchAttendants();
    })();
  }, []);

  const handleCircleClick = (attendant: AttendantListItem) => {
    trackPageView('/edit-attendant', 'Edit Attendant');
    navigate(`/edit-attendant/${attendant.employeeId}`, {
      state: { attendant }
    });
  };

  const handleCreateClick = () => {
    navigate('/create-attendant');
    trackPageView('/create-attendant', 'Create Attendant');
  };

  return (
    <ContentBlock hasBackButton loading={loading}>
      <div className={classes.tabTitle}>Attendants</div>
      <Tabs
        labels={[TabLabels.Active, TabLabels.Inactive]}
        value={activeTab}
        onChange={setActiveTab}
      />
      <div className={classes.circleContainer}>
        {activeTab === TabLabels.Active ? (
          <>
            <div>
              <div
                className={classes.newAttendantButton}
                onClick={() => {
                  handleCreateClick();
                }}
              >
                <GoPlus className={classes.plusIcon} />
              </div>
              <div className={classes.personName}>+ Add New</div>
            </div>
            {active?.map((person) => (
              <div key={person.name} onClick={() => handleCircleClick(person)}>
                <div className={classes.personCircle}>
                  <div className={classes.innerCircle}>
                    {GetInitials(person.name)}
                  </div>
                </div>
                <div className={classes.personName}>
                  {SetInitialToUpperCase(person.name)}
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            {inactive?.map((person) => (
              <div key={person.name} onClick={() => handleCircleClick(person)}>
                <div className={classes.personCircle}>
                  <div className={classes.innerCircle}>
                    {GetInitials(person.name)}
                  </div>
                </div>
                <div className={classes.personName}>{person.name}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </ContentBlock>
  );
};

// Helper function to extract initials from the name
export function GetInitials(name: string): string {
  // Convert innitials to uppercase
  if (!name?.trim()) return '';

  return name
    .trim()
    .split(' ')
    .filter((n) => n)
    .map((n) => n[0].toUpperCase())
    .join('');
}

export function SetInitialToUpperCase(name: string): string {
  if (!name?.trim()) return '';

  return name
    .trim()
    .split(' ')
    .filter((n) => n)
    .map((n) => n[0].toUpperCase() + n.slice(1))
    .join(' ');
}
